import { LoadingOutlined } from "@ant-design/icons";
import { Empty, notification, Pagination, Select, Tooltip } from "antd";
import classNames from "classnames";
import AccessControl from "components/common/AccessControl/accessControl";
import FilterPopover from "components/common/FilterPopover/container";
import SVG from "components/common/SVG";
import { allUserRoles, clientRoles, ENTITY_TYPE, ROLE, roleLabels, userRoleType } from "constants/phase";
import { debounce } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";

import DeleteModal from "../../../../components/common/DeleteModal";
import AddUserModal from "../../../../components/common/Modals/AddUserModal";
import AssignUserModal from "../../../../components/common/Modals/AssignUserModal";
import { convertIsoDateToStandardFormat } from "../../../../utils/date";
import styles from "./style.module.scss";

const pageSize = 5;

const roleSelectOptions = (role, isAdmin = false, superAdmin = false) => {
  if (Object.values(clientRoles).includes(role) && superAdmin) {
    return Object.values(clientRoles).map((role) => ({ label: roleLabels[role], value: role }));
  } else if (Object.values(clientRoles).includes(role)) {
    return Object.values(clientRoles)
      .map((role) => ({ label: roleLabels[role], value: role }))
      .filter((role) => isAdmin && role.value !== allUserRoles.admin);
  } else if (role === userRoleType.generalOps || role === userRoleType.privilegeOps) {
    return [{ value: role, label: roleLabels[role] }];
  }

  return [];
};

const Rows = ({ users, updateUserRole, updateUserHandler, setShowRemoveUserModal, isAdmin, isSuperAdmin }) => {
  const { currentRole } = useOutletContext();
  return users.map((user) => {
    return (
      <tr key={user.userId}>
        <td>{user?.name}</td>
        <td>{user?.createdAt ? convertIsoDateToStandardFormat(user?.createdAt) : ""}</td>
        <td>{user?.email}</td>
        <td>
          <Select
            showSearch={false}
            allowClear={false}
            disabled={
              currentRole === ROLE.USER ||
              currentRole === ROLE.REVIEWER ||
              user?.role === userRoleType.privilegeOps ||
              user?.role === userRoleType.generalOps ||
              (isAdmin && user?.role === allUserRoles.admin)
            }
            optionFilterProp="children"
            style={{ maxWidth: "140px" }}
            className="custom-ant-select-v2"
            value={user?.role}
            onChange={(role) => updateUserRole(user.userId, role)}
            options={roleSelectOptions(user?.role, isAdmin, isSuperAdmin)}
          />
        </td>
        <td>
          <div className={styles.actionContainer}>
            <AccessControl code="update_user" page="organization-profile">
              <Tooltip placement="top" title={"Update User Role"} arrow>
                <button
                  className={classNames(styles.updateCta, {
                    [styles.disabledCta]:
                      user?.role === userRoleType.privilegeOps ||
                      user?.role === userRoleType.generalOps ||
                      (isAdmin && user?.role === allUserRoles.admin),
                  })}
                  onClick={() => updateUserHandler(user)}
                >
                  Update
                </button>
              </Tooltip>
            </AccessControl>

            {!(isAdmin && user?.role === allUserRoles.admin) && (
              <AccessControl code="remove_user" page="organization-profile">
                <Tooltip placement="top" title={"Remove User"} arrow>
                  <div className={styles.item} onClick={() => setShowRemoveUserModal(user)}>
                    <SVG className={styles.actionIcon} iconName={"images/remove.svg"} />
                  </div>
                </Tooltip>
              </AccessControl>
            )}
          </div>
        </td>
      </tr>
    );
  });
};

const Users = ({
  getOrganizationUsers,
  getWorkspaceUsers,
  organizationId,
  updateOrganizationMember,
  removeOrganizationMember,
  organizationDetails,
  entityConfig,
}) => {
  const { currentRole, selectedEntity } = useOutletContext();
  const isSuperAdmin = currentRole === allUserRoles.superAdmin;
  const isAdmin = currentRole === allUserRoles.admin;
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(null);
  const [search, setSearch] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(null);
  const [showAssignUserModal, setShowAssignUserModal] = useState(null);
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);

  const updateUserRole = (userId, role) => {
    setUsers((prev) =>
      prev.map((user) => {
        if (user.userId === userId) {
          return { ...user, role };
        }

        return user;
      }),
    );
  };

  const updateUserHandler = useCallback(
    async (user) => {
      try {
        const payload = {
          organizationMemberId: user?.organizationMemberId,
          role: user?.role,
        };

        const response = await updateOrganizationMember(payload);

        if (response?.value?.status) {
          notification.success({ message: response?.value?.message });
        }
      } catch (error) {
        console.log(error);
        if (error.message) {
          notification.error({ message: error.message });
        }
      } finally {
      }
    },
    [updateOrganizationMember],
  );

  const fetchOrganizationUsers = useCallback(
    async ({ page = 1, search = "", dateRange = [], roles = [] }) => {
      try {
        setLoading(true);

        const commonPayload = {
          pageSize,
          page,
          search,
          fromDate: dateRange ? dateRange[0] : null,
          toDate: dateRange ? dateRange[1] : null,
          roles: roles?.length ? roles : undefined,
          roleType: isSuperAdmin ? undefined : userRoleType.client,
        };

        let specificPayload = {};
        let fetchUsers;

        if (id.startsWith("org")) {
          specificPayload = { organizationId: id };
          fetchUsers = getOrganizationUsers;
        } else if (id.startsWith("ws")) {
          specificPayload = { workspaceId: id, role: currentRole };
          fetchUsers = getWorkspaceUsers;
        }

        const payload = { ...commonPayload, ...specificPayload };
        const response = await fetchUsers(payload);

        if (response?.value?.status) {
          setUsers(response?.value?.users);
          setTotal(response?.value?.total);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [getOrganizationUsers, getWorkspaceUsers, isSuperAdmin, organizationId, id, currentRole, pageSize],
  );

  const handleRemoveUser = useCallback(
    async (organizationMemberId, search = "") => {
      try {
        setLoading(true);
        const payload = {
          organizationMemberId,
        };

        const response = await removeOrganizationMember(payload);
        if (response?.value?.status) {
          notification.success({ message: response?.value?.message });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
        setShowRemoveUserModal(null);
        fetchOrganizationUsers({ page: 1, search });
        setActivePage(1);
      }
    },
    [activePage, fetchOrganizationUsers, removeOrganizationMember],
  );

  const debouncedFetchWorkspaces = useMemo(
    () =>
      debounce((search) => {
        setActivePage(1);
        fetchOrganizationUsers({ page: 1, search });
      }, 800),
    [fetchOrganizationUsers],
  );

  const onSearch = (e) => {
    setSearch(e.target.value);
    debouncedFetchWorkspaces(e.target.value);
  };

  const closeAndFetchAddUserModal = useCallback(() => {
    setShowAddUserModal(null);
    fetchOrganizationUsers({ page: activePage, search });
  }, [activePage, fetchOrganizationUsers, search]);

  const closeAndFetchAssignUserModal = useCallback(() => {
    setShowAssignUserModal(null);
    fetchOrganizationUsers({ page: activePage, search });
  }, [activePage, fetchOrganizationUsers, search]);

  return (
    <>
      <div className={styles.container}>
        <div className="title-block--op">
          <div>
            <h4>Users</h4>
          </div>
          <div className={styles.rightContainer}>
            <div className={styles.searchContainer}>
              <div className={styles.leftCont}>
                <SVG className={styles.filterIcon} iconName={"images/search.svg"} />
                <input
                  type="search"
                  name="org-search"
                  id="org-search"
                  placeholder="Search"
                  autoFocus
                  value={search}
                  onChange={onSearch}
                  className={styles.search}
                />
              </div>
              <FilterPopover
                dateRange={filterDateRange}
                setDateRange={setFilterDateRange}
                setIsModalOpen={setFilterPopupOpen}
                fetch={fetchOrganizationUsers}
                isUserComponent={true}
                activePage={activePage}
                setActivePage={setActivePage}
                isModalOpen={filterPopupOpen}
              />
            </div>
            <div className={styles.ctaContainer}>
              <AccessControl code="add_user" page="organization-profile">
                <button
                  className={styles.addUserCta}
                  onClick={() =>
                    entityConfig?.maxNumberOfUsers > total
                      ? setShowAddUserModal(selectedEntity || organizationDetails)
                      : notification.warning({
                          message: `You can add only ${entityConfig?.maxNumberOfUsers ? entityConfig?.maxNumberOfUsers : 3} Users.`,
                        })
                  }
                >
                  Add new user
                </button>
              </AccessControl>
              <AccessControl code="assign_user" page="organization-profile">
                <button
                  className={styles.assignUserCta}
                  onClick={() =>
                    entityConfig?.maxNumberOfUsers > total
                      ? setShowAssignUserModal(selectedEntity || organizationDetails)
                      : notification.warning({
                          message: `You can add only ${entityConfig?.maxNumberOfUsers ? entityConfig?.maxNumberOfUsers : 3} Users.`,
                        })
                  }
                >
                  Assign user
                </button>
              </AccessControl>
            </div>
          </div>
        </div>
        {loading ? (
          <div className={styles.emptyContainer}>
            <LoadingOutlined style={{ fontSize: 30, align: "center", display: "inline-flex" }} spin />
          </div>
        ) : users.length === 0 ? (
          <div className={styles.emptyContainer}>
            <Empty />
          </div>
        ) : (
          <div className="table-resposive table-container user-management-table normal-table-block">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>created AT</th>
                  <th>Email ID</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <Rows
                  users={users}
                  updateUserRole={updateUserRole}
                  updateUserHandler={updateUserHandler}
                  setShowRemoveUserModal={setShowRemoveUserModal}
                  isAdmin={isAdmin}
                  isSuperAdmin={isSuperAdmin}
                />
              </tbody>

              <tfoot></tfoot>
            </table>
          </div>
        )}
      </div>

      {users.length > 0 && (
        <Pagination
          showSizeChanger={false}
          className={styles.pagination}
          current={activePage}
          pageSize={pageSize}
          onChange={(activePage) => setActivePage(activePage)}
          total={total}
        />
      )}

      {showAddUserModal && (
        <AddUserModal
          showModal={true}
          entity={showAddUserModal}
          entityType={ENTITY_TYPE.ORGANIZATION}
          closeAndFetchModal={closeAndFetchAddUserModal}
          setShowModal={setShowAddUserModal}
          isAdmin={isAdmin}
        />
      )}

      {showAssignUserModal && (
        <AssignUserModal
          entity={showAssignUserModal}
          entityType={ENTITY_TYPE.ORGANIZATION}
          closeAndFetchModal={closeAndFetchAssignUserModal}
          setShowModal={setShowAssignUserModal}
          isAdmin={isAdmin}
        />
      )}

      {showRemoveUserModal && (
        <DeleteModal
          id={showRemoveUserModal?.organizationMemberId}
          setId={setShowRemoveUserModal}
          handleDelete={() => handleRemoveUser(showRemoveUserModal?.organizationMemberId, search)}
          content={`Are you sure you want to remove the user - ${showRemoveUserModal?.name} from the organization ?`}
        />
      )}
    </>
  );
};

export default Users;
